import { UploadFile } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, styled } from '@mui/material';
import { ChangeEvent, useState } from "react";
import toast from "../../hooks/ToastCustom";
import { useCreateSubmissionBatchMutation } from "../../services/submissions.service";

const StyledDialog = styled(Dialog)(({ theme }) => ({
    "& .MuiPaper-root": {
      width: "80%",
    },
    "& .MuiDialogContent-root": {
      padding: theme.spacing(2),
    },
    "& .MuiDialogActions-root": {
      padding: theme.spacing(1),
    },
  }));
  const KNOWN_FIELDS = [
    "passport.number",
    "fullName",
    "email",
    "nationality",
    "country",
    "phone",
    "degree",
    "program",
  ];
  // The backend schema marks these as required; a row missing any of them is
  // rejected by mongoose with a 500, so filter them out before importing.
  const MANDATORY_FIELDS = [
    "fullName",
    "email",
    "country",
    "phone",
    "degree",
    "program",
  ];

  type ParseResult = {
    rows: any[];
    missingColumns: string[];
    skippedRows: number;
  };

  function detectDelimiter(headerLine: string) {
    const candidates = [",", ";", "\t"];
    return candidates.reduce((best, candidate) =>
      headerLine.split(candidate).length > headerLine.split(best).length
        ? candidate
        : best
    );
  }

  const clean = (value?: string) =>
    (value ?? "").replace(/^﻿/, "").replace(/^"|"$/g, "").trim();

  // Quote-aware split: a naive line.split(",") shifts every column to the right
  // when a value itself contains the delimiter (e.g. "Doe, John"), which used to
  // import silently wrong data.
  function splitLine(line: string, delimiter: string) {
    const cells: string[] = [];
    let current = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i += 1) {
      const char = line[i];
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i += 1;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === delimiter && !inQuotes) {
        cells.push(current);
        current = "";
      } else {
        current += char;
      }
    }
    cells.push(current);
    return cells;
  }

  function csvToArr(stringValue: string): ParseResult {
    const lines = stringValue
      // strip BOM and normalise CRLF / CR line endings so header keys don't
      // end up as "program\r" and silently stop matching
      .replace(/^﻿/, "")
      .replace(/\r\n?/g, "\n")
      .split("\n")
      .filter((line) => line.trim() !== "");

    if (!lines.length) {
      return { rows: [], missingColumns: MANDATORY_FIELDS, skippedRows: 0 };
    }

    const delimiter = detectDelimiter(lines[0]);
    const [headerLine, ...rest] = lines;
    const keys = splitLine(headerLine, delimiter).map(clean);

    const missingColumns = MANDATORY_FIELDS.filter((f) => !keys.includes(f));
    if (missingColumns.length) {
      return { rows: [], missingColumns, skippedRows: 0 };
    }

    let skippedRows = 0;
    const rows = rest.reduce<any[]>((acc, line) => {
      const cells = splitLine(line, delimiter);
      // more cells than headers means the row is malformed — importing it would
      // put values under the wrong fields
      if (cells.length > keys.length) {
        skippedRows += 1;
        return acc;
      }
      const object: any = {};
      keys.forEach((key, index) => {
        if (KNOWN_FIELDS.includes(key)) {
          object[key] = clean(cells[index]);
        }
      });
      if (MANDATORY_FIELDS.some((field) => !object[field])) {
        skippedRows += 1;
        return acc;
      }
      acc.push(object);
      return acc;
    }, []);

    return { rows, missingColumns: [], skippedRows };
  }
function SubmissionsImport({handleClose,open}:{handleClose:any,open:boolean}) {
const [dataArray, setAataArray] = useState<any>([])
const [missingColumns, setMissingColumns] = useState<string[]>([])
const [skippedRows, setSkippedRows] = useState(0)
const[createSubmissionBatch]= useCreateSubmissionBatchMutation()
const handleReadFile=(e: ChangeEvent<HTMLInputElement>)=>{
const files=e.target.files?.[0];
if(files){
    const fileReader = new FileReader();
    fileReader.readAsText(files)
    fileReader.onload = function (e:ProgressEvent<FileReader>) {
        // Access to content with e.target.result
        const result=e.target?.result as string
        const { rows, missingColumns, skippedRows } = csvToArr(result);
        setAataArray(rows)
        setMissingColumns(missingColumns)
        setSkippedRows(skippedRows)
        if (missingColumns.length) {
          toast.error(
            `Missing column(s): ${missingColumns.join(", ")}. Export the file as CSV with those exact headers.`
          );
        } else if (!rows.length) {
          toast.error("No importable rows found in this file.");
        } else if (skippedRows) {
          toast.warning(`${skippedRows} row(s) skipped — required fields empty.`);
        }
      };
}

}
const handleImport=async()=>{
if(!dataArray.length || missingColumns.length){
    toast.error("Nothing to import — upload a CSV with the required columns first.")
    return
}
try{
    await createSubmissionBatch(dataArray).unwrap()
    toast.success(`${dataArray.length} submission(s) imported.`)
    handleClose()
}catch(error){
    // the global RTK Query error middleware already surfaces the message
}
}
  return (
    <StyledDialog open={open}>
      <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
        <Stack direction={"row"} alignItems={"center"}>
          Import Submissions
          <Button startIcon={<UploadFile />} component="label">
            Upload File
            <input type="file" accept=".csv,text/csv" hidden onChange={handleReadFile} />
          </Button>
        </Stack>
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}>
        <CloseIcon />
      </IconButton>
      <DialogContent dividers>
        {missingColumns.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            The file is missing the column(s):{" "}
            <strong>{missingColumns.join(", ")}</strong>. Save it as a
            comma-separated CSV whose header row uses these exact names:{" "}
            {MANDATORY_FIELDS.join(", ")}.
          </Alert>
        )}
        {skippedRows > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {skippedRows} row(s) were skipped because a required field was
            empty.
          </Alert>
        )}
        {dataArray && (
          <table
            style={{
              border: "1px solid black",
            }}>
            <thead
              style={{
                border: "1px solid black",
              }}>
              <tr
                style={{
                  border: "1px solid black",
                  borderCollapse: "collapse",
                }}
                key={"header"}>
                {dataArray[0] &&
                  Object.keys(dataArray[0]).map((key: any) => (
                    <th
                      style={{
                        border: "1px solid black",
                        borderCollapse: "collapse",
                      }}
                      key={key}>
                      {key}
                    </th>
                  ))}
              </tr>
            </thead>
            <tbody>
              {dataArray &&
                dataArray.map((item: any, idx: number) => (
                  <tr
                    style={{
                      border: "1px solid black",
                      borderCollapse: "collapse",
                    }}
                    key={idx}>
                    {item &&
                      Object.values(item).map((cell: any, idx: number) => {
                        return (
                          <td
                            style={{
                              border: "1px solid black",
                              borderCollapse: "collapse",
                            }}
                            key={idx}>
                            {cell}
                          </td>
                        );
                      })}
                  </tr>
                ))}
            </tbody>
          </table>
        )}
      </DialogContent>
      <DialogActions>
        <Button autoFocus onClick={handleClose}>
          Cancel
        </Button>
        <Button
          type="button"
          disabled={!dataArray.length || missingColumns.length > 0}
          onClick={handleImport}>
          Import{dataArray.length ? ` (${dataArray.length})` : ""}
        </Button>
      </DialogActions>
    </StyledDialog>
  );
}

export default SubmissionsImport